'use client'

import { useState } from 'react'
import Link from 'next/link'
import { recommend, type RecommendParams } from '@/lib/api'
import type { ContentItem } from '@/lib/types'
import { ChipGroup } from '@/components/ui/ChipGroup'
import { PosterImage } from '@/components/ui/PosterImage'
import { FavoriteButton } from '@/components/ui/FavoriteButton'
import { detailPath, formatRating, formatRuntime, typeLabel } from '@/lib/utils'
import {
  WIZARD_TYPE_OPTIONS,
  WIZARD_GENRE_OPTIONS,
  MOOD_OPTIONS,
  MOVIE_DURATION_OPTIONS,
  SERIES_DURATION_OPTIONS,
  ERA_OPTIONS,
} from '@/lib/constants'

type Phase = 'wizard' | 'loading' | 'result' | 'notfound' | 'error'

interface Answers {
  type: string
  genre: string
  mood: string
  duration: string
  era: string
}

const STEPS = ['Ne arıyorsun?', 'Hangi tür?', 'Modun nasıl?', 'Süre tercihi', 'Yeni mi klasik mi?']

export default function OnerPage() {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>({
    type: 'farketmez',
    genre: 'Farketmez',
    mood: '',
    duration: 'farketmez',
    era: 'farketmez',
  })
  const [phase, setPhase] = useState<Phase>('wizard')
  const [result, setResult] = useState<ContentItem | null>(null)
  const [reasonGenre, setReasonGenre] = useState('')
  const [relaxed, setRelaxed] = useState(false)
  const [excludeIds, setExcludeIds] = useState<string[]>([])

  const set = (key: keyof Answers, value: string) =>
    setAnswers((a) => ({ ...a, [key]: value }))

  const durationOptions =
    answers.type === 'series' ? SERIES_DURATION_OPTIONS : MOVIE_DURATION_OPTIONS

  const runRecommend = async (exclude: string[]) => {
    setPhase('loading')
    const params: RecommendParams = {
      type: answers.type,
      genre: answers.genre !== 'Farketmez' ? answers.genre : undefined,
      mood: answers.mood || undefined,
      duration: answers.duration !== 'farketmez' ? answers.duration : undefined,
      era: answers.era !== 'farketmez' ? answers.era : undefined,
      exclude,
    }
    try {
      const res = await recommend(params)
      if (res.found && res.item) {
        setResult(res.item)
        setReasonGenre(res.reasonGenre || '')
        setRelaxed(Boolean(res.relaxed))
        setExcludeIds((prev) => [...prev, res.item!.id])
        setPhase('result')
      } else {
        setPhase('notfound')
      }
    } catch (e) {
      console.error('[oner]', e)
      setPhase('error')
    }
  }

  const start = () => {
    setExcludeIds([])
    runRecommend([])
  }

  const another = () => runRecommend(excludeIds)

  const restart = () => {
    setStep(0)
    setPhase('wizard')
    setResult(null)
    setExcludeIds([])
  }

  // ---------------- RESULT ----------------
  if (phase === 'result' && result) {
    return (
      <ResultView
        item={result}
        reasonGenre={reasonGenre}
        relaxed={relaxed}
        answers={answers}
        onAnother={another}
        onRestart={restart}
      />
    )
  }

  if (phase === 'loading') {
    return (
      <div className="container-page grid min-h-[60vh] place-items-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-white/10 border-t-crimson-500" />
          <p className="font-display text-lg font-semibold text-white">Senin için arıyoruz...</p>
          <p className="mt-1 text-sm text-neutral-500">Seçimlerine uygun bir şey buluyoruz</p>
        </div>
      </div>
    )
  }

  if (phase === 'notfound' || phase === 'error') {
    return (
      <div className="container-page grid min-h-[60vh] place-items-center">
        <div className="max-w-md rounded-2xl border border-white/10 bg-ink-800/60 p-10 text-center">
          <div className="mb-3 text-4xl">{phase === 'error' ? '⚠️' : '🤔'}</div>
          <h2 className="font-display text-xl font-bold text-white">
            {phase === 'error' ? 'Bir sorun oluştu' : 'Başka sonuç bulamadık'}
          </h2>
          <p className="mt-2 text-sm text-neutral-400">
            {phase === 'error'
              ? 'Öneri getirilirken bir sorun oluştu. Lütfen tekrar deneyin.'
              : 'Bu kriterlere uygun başka sonuç bulamadık. Farklı seçimlerle tekrar dene.'}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {phase === 'error' && (
              <button onClick={another} className="btn-primary">Tekrar Dene</button>
            )}
            <button onClick={restart} className="btn-ghost">Baştan Başla</button>
          </div>
        </div>
      </div>
    )
  }

  // ---------------- WIZARD ----------------
  return (
    <div className="container-page py-10">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            🎲 Bana Bir Şey Bul
          </h1>
          <p className="mt-2 text-neutral-400">
            Birkaç soruya cevap ver, sana uygun film veya diziyi bulalım.
          </p>
        </div>

        {/* progress */}
        <div className="mb-8 flex items-center justify-center gap-2">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-8 bg-crimson-500' : i < step ? 'w-4 bg-crimson-700' : 'w-4 bg-white/10'
              }`}
            />
          ))}
        </div>

        <div className="surface animate-fade-in p-6 sm:p-8">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-crimson-400">
            {step + 1}. Soru / {STEPS.length}
          </p>
          <h2 className="mb-5 font-display text-xl font-bold text-white">{STEPS[step]}</h2>

          {step === 0 && (
            <ChipGroup size="md" options={WIZARD_TYPE_OPTIONS} value={answers.type} onChange={(v) => set('type', v)} />
          )}
          {step === 1 && (
            <ChipGroup size="md" options={WIZARD_GENRE_OPTIONS} value={answers.genre} onChange={(v) => set('genre', v)} />
          )}
          {step === 2 && (
            <ChipGroup size="md" options={MOOD_OPTIONS} value={answers.mood} onChange={(v) => set('mood', v)} />
          )}
          {step === 3 && (
            <ChipGroup size="md" options={durationOptions} value={answers.duration} onChange={(v) => set('duration', v)} />
          )}
          {step === 4 && (
            <ChipGroup size="md" options={ERA_OPTIONS} value={answers.era} onChange={(v) => set('era', v)} />
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="btn-ghost disabled:opacity-40"
            >
              ← Geri
            </button>

            {step < STEPS.length - 1 ? (
              <button onClick={() => setStep((s) => s + 1)} className="btn-primary">
                Devam →
              </button>
            ) : (
              <button onClick={start} className="btn-primary text-base">
                🎲 BANA BİR ŞEY BUL
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Result view
// ============================================================================
function buildReason(answers: Answers, reasonGenre: string, relaxed: boolean): string {
  if (relaxed) {
    return 'Tam kriterlerine birebir uyan az sonuç vardı, bu yüzden sana en yakın olan bu seçkin yapımı önerdik.'
  }
  const bits: string[] = []
  if (answers.type === 'movie') bits.push('film')
  else if (answers.type === 'series') bits.push('dizi')
  if (reasonGenre) bits.push(`${reasonGenre.toLocaleLowerCase('tr')} türünde bir şey`)
  const moodLabel = MOOD_OPTIONS.find((m) => m.value === answers.mood)?.label
  if (moodLabel) bits.push(`"${moodLabel.replace(/^[^\s]+\s/, '')}" moduna uygun`)
  if (answers.era === 'son') bits.push('son yıllardan')
  else if (answers.era === '2000') bits.push("2000'lerden")
  else if (answers.era === '90') bits.push("90'lar ve öncesinden")

  if (bits.length === 0) return 'Öne çıkan, yüksek puanlı bir yapım olduğu için bunu önerdik.'
  return `Seçimlerini göz önünde bulundurarak bunu önerdik çünkü ${bits.join(', ')} istedin.`
}

function ResultView({
  item,
  reasonGenre,
  relaxed,
  answers,
  onAnother,
  onRestart,
}: {
  item: ContentItem
  reasonGenre: string
  relaxed: boolean
  answers: Answers
  onAnother: () => void
  onRestart: () => void
}) {
  return (
    <div className="relative">
      {item.backdrop && (
        <div className="pointer-events-none absolute inset-0 h-[420px] overflow-hidden opacity-30">
          <PosterImage src={item.backdrop} alt="" ratio="h-[420px]" eager />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-900/40 via-ink-900/80 to-ink-900" />
        </div>
      )}

      <div className="container-page relative py-10">
        <p className="mb-6 text-center font-display text-sm font-semibold uppercase tracking-widest text-crimson-400">
          🎬 Bugün Bunu Seçtik
        </p>

        <div className="mx-auto grid max-w-4xl gap-8 sm:grid-cols-[260px_1fr]">
          <div className="mx-auto w-full max-w-[260px]">
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-glow">
              <PosterImage src={item.poster} alt={item.title} eager />
            </div>
          </div>

          <div className="animate-fade-in">
            <span
              className={`mb-3 inline-block rounded-md px-2 py-1 text-xs font-semibold uppercase ${
                item.type === 'series' ? 'bg-sky-500/80' : 'bg-crimson-600/80'
              } text-white`}
            >
              {typeLabel(item.type)}
            </span>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {item.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-neutral-300">
              {item.rating ? (
                <span className="flex items-center gap-1 font-semibold text-amber-300">
                  ⭐ {formatRating(item.rating)}
                </span>
              ) : null}
              {item.year && <span>📅 {item.year}</span>}
              {item.runtime && <span>⏱️ {formatRuntime(item.runtime, item.runtimeMinutes)}</span>}
            </div>

            {item.genres.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {item.genres.map((g) => (
                  <span key={g} className="chip">🎭 {g}</span>
                ))}
              </div>
            )}

            {item.overview && (
              <p className="mt-5 line-clamp-3 text-sm leading-relaxed text-neutral-300">{item.overview}</p>
            )}

            <div className="mt-6 rounded-xl border border-crimson-900/30 bg-crimson-950/20 p-4">
              <p className="text-sm leading-relaxed text-neutral-200">
                {buildReason(answers, reasonGenre, relaxed)}
              </p>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link href={detailPath(item.type, item.id)} className="btn-primary">
                ▶ Detayları Gör
              </Link>
              <button onClick={onAnother} className="btn-ghost">🔄 Başka Öner</button>
              <FavoriteButton item={item} variant="full" />
            </div>

            <button onClick={onRestart} className="mt-5 text-sm text-neutral-500 hover:text-white">
              ← Baştan başla
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
